import axios from 'axios';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

const INITIAL_SNAPSHOT = {
  filters: [],
  limit: 5,
};

function normalizeSavedFiltersResponse(data) {
  return {
    filters: Array.isArray(data?.filters) ? data.filters : [],
    limit: Number(data?.limit) || 5,
  };
}

async function fetchSavedSecFilingFilters(apiBaseUrl, getAccessTokenSilently, signal) {
  const token = await getAccessTokenSilently({ detailedResponse: false });
  const endpoint = `${apiBaseUrl}/api/sec-filings/filters`;
  const { data } = await axios.get(endpoint, {
    signal,
    headers: { Authorization: `Bearer ${token}` },
  });
  return normalizeSavedFiltersResponse(data);
}

function createSavedFiltersStore(apiBaseUrl, getAccessTokenSilently, isAuthenticated) {
  let snapshot = INITIAL_SNAPSHOT;
  let controller = null;
  let requestId = 0;
  const listeners = new Set();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const setSnapshot = (nextSnapshot) => {
    snapshot = nextSnapshot;
    notify();
  };

  const loadSavedFilters = async () => {
    if (!isAuthenticated) {
      setSnapshot(INITIAL_SNAPSHOT);
      return;
    }

    if (controller) controller.abort();
    const activeController = new AbortController();
    controller = activeController;
    const currentRequestId = requestId + 1;
    requestId = currentRequestId;

    try {
      const nextSnapshot = await fetchSavedSecFilingFilters(
        apiBaseUrl,
        getAccessTokenSilently,
        activeController.signal
      );
      if (activeController.signal.aborted || currentRequestId !== requestId) return;
      setSnapshot(nextSnapshot);
    } catch (err) {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return;
      if (currentRequestId !== requestId) return;
      setSnapshot(INITIAL_SNAPSHOT);
    }
  };

  return {
    getSnapshot: () => snapshot,
    setFilters: (updater) => {
      setSnapshot({
        ...snapshot,
        filters:
          typeof updater === 'function'
            ? updater(snapshot.filters)
            : updater,
      });
    },
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1) loadSavedFilters();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && controller) {
          controller.abort();
          controller = null;
        }
      };
    },
  };
}

export function useSavedSecFilingFiltersData(
  apiBaseUrl,
  getAccessTokenSilently,
  isAuthenticated
) {
  const store = useMemo(
    () => createSavedFiltersStore(apiBaseUrl, getAccessTokenSilently, isAuthenticated),
    [apiBaseUrl, getAccessTokenSilently, isAuthenticated]
  );
  const { filters, limit } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );

  const setSavedCustomFilters = useCallback((updater) => {
    store.setFilters(updater);
  }, [store]);

  return {
    savedCustomFilterLimit: limit,
    savedCustomFilters: filters,
    setSavedCustomFilters,
  };
}
